import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { formatClock, team, type Fan, type GameEvent, type GameMedia, type ReactionEmoji, type RoomSnapshot, type Side, type Take } from '@clutch/core';
import { ReactionPicker } from '../room/ReactionPicker';
import { useRoom, type LockUi } from '../room/useRoom';
import {
  chanceView,
  choiceLabel,
  choiceSide,
  cutoffLabel,
  firstName,
  friendCalls,
  initials,
  lockStamp,
  resolutionCopy,
  sideName,
} from '../room/derive';
import { readableOn, teamAccent } from '../ui/color';
import { navigate } from '../router';
import { useClient, store } from '../client';
import { BuzzerOverlay, RewindOverlay, Stacks, Stage, finalPlay, reducedMotion, useRewindPlayer } from '../stage/Stage';
import { anchorReactions, challengeText, fansVsModel, type ReactionStack } from '../stage/social';
import { STAGE_TYPES, playCaption } from '../stage/court';
import { rewindSteps } from '../stage/rewind';
import { useReactCam } from '../studio/recorder';
import { QUICK_TAKE_MS } from '../studio/marks';
import { CONSENT_KEY, CamBubble, CamConsent, TakeBubbles, TakePlayer, TakeReview } from '../studio/Studio';
import { DanmuLane, useDanmu } from '../room/DanmuLane';
import { takeAnchorSeq, takesCovering, type DanmuItem } from '../room/danmu';
import { HighlightsSheet, OfficialVideo, clipFor, useGameMedia } from '../media/Highlights';
import { ReelSheet } from '../reel/ReelSheet';
import { selectReelSegments } from '../reel/segments';
import { Headshot } from '../ui/Media';
import { AppLink, FanNav } from '../ui/Nav';
import { beatFor, beatLabel, celebrate, type Beat } from '../stage/beats';

export const WATCH_LIVE_URL = 'https://www.nba.com/watch';

/** Bring a confirmation into view (instant under reduced motion). */
function revealAboveReactbar(el: HTMLElement | null) {
  if (!el) return;
  el.scrollIntoView({ block: 'center', behavior: reducedMotion() ? 'auto' : 'smooth' });
}

/** Buzzer lands on the court first, then the result card. */
const BUZZER_MS = 2200;
const BUZZER_MS_REDUCED = 900;

export { feedBadge } from '../room/feed-badge';
import { feedBadge } from '../room/feed-badge';

export function Room({ roomId, fan }: { roomId: string; fan: Fan }) {
  const client = useClient();
  const cam = useReactCam();
  const pushDanmu = useRef<(x: DanmuItem) => void>();
  // Big-moment beats: a made basket by your team, a lead change, a tie. Live events only.
  const [beat, setBeat] = useState<Beat & { key: number; label: string }>();
  const lastScore = useRef<{ homeScore: number; awayScore: number }>();
  const lastBeatAt = useRef(0);
  const metaRef = useRef<{ home: string; away: string }>();
  const onBeatEvent = (e: GameEvent) => {
    const prev = lastScore.current;
    lastScore.current = { homeScore: e.homeScore, awayScore: e.awayScore };
    const meta = metaRef.current;
    if (!meta) return;
    const mySide = fan.team === meta.home ? 'home' : fan.team === meta.away ? 'away' : undefined;
    const b = beatFor(e, prev, mySide);
    if (!b) return;
    const now = Date.now();
    if (b.strength < 2 && now - lastBeatAt.current < 1500) return; // fast replays: don't strobe
    lastBeatAt.current = now;
    setBeat({ ...b, key: now, label: beatLabel(b, team(b.side === 'home' ? meta.home : meta.away).name) });
    if (b.mine || b.kind === 'lead_change') celebrate(b.strength === 2 ? 'big' : 'small');
  };
  const onBeatRef = useRef(onBeatEvent);
  onBeatRef.current = onBeatEvent;
  const r = useRoom(roomId, fan, {
    // Marks: the plays a take covers (not substitutions or rebounds).
    onGameEvent: (e) => {
      onBeatRef.current(e);
      if (STAGE_TYPES.has(e.type) && e.type !== 'period_start') cam.mark(e.seq);
    },
    onTake: (t) => {
      if (t.fanId !== fan.fanId) pushDanmu.current?.({ id: t.takeId, kind: 'take', seq: t.endedAtSeq, fanId: t.fanId, name: firstName(t.displayName), takeId: t.takeId });
    },
  });
  const s = r.snapshot;
  metaRef.current = s ? { home: s.meta.home.tricode, away: s.meta.away.tricode } : undefined;
  useEffect(() => {
    if (!beat) return;
    const t = setTimeout(() => setBeat(undefined), reducedMotion() ? 1200 : 1900);
    return () => clearTimeout(t);
  }, [beat]);
  const [dismissed, setDismissed] = useState(false);
  const [full, setFull] = useState(false);
  const [pillOpen, setPillOpen] = useState(false);
  const [toast, setToast] = useState<string>();
  const stageRef = useRef<HTMLDivElement>(null);
  const media = useGameMedia(s?.gameId);
  const [highlights, setHighlights] = useState<{ start?: number }>();
  const [consent, setConsent] = useState<{ quick: boolean }>();
  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState<string>();
  const [localUrls, setLocalUrls] = useState<Record<string, string>>({});
  const [reelTake, setReelTake] = useState<Take>();
  const [player, setPlayer] = useState<{ take: Take; offsetMs?: number }>();
  const [controlling, setControlling] = useState(false);

  const flash = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(undefined), 2600);
  }, []);

  // Rewind: real beats from the server, played on the court.
  const steps = useMemo(() => rewindSteps(r.rewind), [r.rewind]);
  const rewinder = useRewindPlayer(steps, r.finishRewind);
  // Arriving from a personal catch-up: the fan has just seen the story, so skip the room recap.
  const [fromCatchUp] = useState(() => new URLSearchParams(location.search).get('from') === 'catchup');
  const skipRewind = rewinder.skip;
  useEffect(() => {
    if (fromCatchUp && steps.length > 0) skipRewind();
  }, [fromCatchUp, steps.length, skipRewind]);

  // Buzzer: 'court' (final play ringed on the stage) → 'card' (result overlay).
  const resId = s?.resolution?.callId;
  const [buzzer, setBuzzer] = useState<'none' | 'court' | 'card'>('none');
  useEffect(() => {
    if (!resId) {
      setBuzzer('none');
      return;
    }
    setBuzzer('court');
    stageRef.current?.scrollIntoView({ block: 'start', behavior: reducedMotion() ? 'auto' : 'smooth' });
    const t = setTimeout(() => setBuzzer('card'), reducedMotion() ? BUZZER_MS_REDUCED : BUZZER_MS);
    return () => clearTimeout(t);
  }, [resId]);

  useEffect(() => {
    if (!full) return;
    const on = (e: KeyboardEvent) => e.key === 'Escape' && setFull(false);
    window.addEventListener('keydown', on);
    return () => window.removeEventListener('keydown', on);
  }, [full]);

  const anchors = useMemo(() => anchorReactions(r.anchored, r.plays), [r.anchored, r.plays]);

  // Danmu: takes and reactions resurface when the game passes their moment again.
  const danmuItems = useMemo<DanmuItem[]>(
    () => [
      ...r.takes.map((t) => ({ id: t.takeId, kind: 'take' as const, seq: takeAnchorSeq(t, s?.probability), fanId: t.fanId, name: t.fanId === fan.fanId ? 'You' : firstName(t.displayName), takeId: t.takeId })),
      ...r.anchored.map((a) => ({ id: `r${a.id}`, kind: 'reaction' as const, seq: a.atSeq, fanId: a.fanId, name: a.mine ? 'You' : firstName(a.name), emoji: a.emoji })),
    ],
    [r.takes, r.anchored, s?.probability, fan.fanId],
  );
  const danmu = useDanmu(danmuItems, s?.state.asOfSeq ?? -1);
  pushDanmu.current = danmu.push;
  const takeById = useMemo(() => new Map(r.takes.map((t) => [t.takeId, t])), [r.takes]);
  const urlOf = useCallback((t: Take) => localUrls[t.takeId] ?? client.takeUrl(t), [localUrls, client]);
  const myTakes = useMemo(() => r.takes.filter((t) => t.fanId === fan.fanId).sort((a, b) => b.createdAt.localeCompare(a.createdAt)), [r.takes, fan.fanId]);
  const myTake = myTakes.find((t) => selectReelSegments(s?.probability ?? [], t.marks, { durationMs: t.durationMs, plays: r.plays }).segments.length > 0);
  const openedTake = useRef<string>();
  useEffect(() => {
    const id = new URLSearchParams(location.search).get('take');
    const take = id ? takeById.get(id) : undefined;
    if (take && openedTake.current !== id) {
      openedTake.current = id!;
      setPlayer({ take });
    }
  }, [takeById]);

  const asOfRef = useRef(-1);
  asOfRef.current = s?.state.asOfSeq ?? -1;
  const consented = () => store.get(CONSENT_KEY) === '1';
  const beginRecording = useCallback(
    async (quick: boolean) => {
      const ok = await cam.enable();
      if (!ok) return flash('Camera unavailable — check your browser’s camera permission.');
      if (quick) cam.start(asOfRef.current, QUICK_TAKE_MS);
    },
    [cam, flash],
  );
  const recordTake = () => {
    if (cam.phase === 'recording') return cam.stop();
    if (!consented()) return setConsent({ quick: true });
    void beginRecording(true);
  };
  const toggleCam = () => {
    if (cam.phase === 'recording') return;
    if (cam.stream) return cam.disable();
    if (!consented()) return setConsent({ quick: false });
    void beginRecording(false);
  };

  const publish = async () => {
    const rec = cam.recording;
    if (!rec || publishing) return;
    setPublishing(true);
    setPublishError(undefined);
    try {
      const take = await client.postTake(
        roomId,
        { fanId: fan.fanId, mimeType: rec.mimeType, durationMs: rec.durationMs, startedAtSeq: rec.startedAtSeq, endedAtSeq: rec.endedAtSeq, marks: rec.marks },
        rec.blob,
      );
      setLocalUrls((u) => ({ ...u, [take.takeId]: rec.url }));
      r.addTake(take);
      cam.keep();
      flash(rec.marks.length ? `Take posted — on ${rec.marks.length} plays` : 'Take posted');
    } catch {
      setPublishError('Couldn’t post your take. Try again.');
    } finally {
      setPublishing(false);
    }
  };

  const share = useCallback(async () => {
    if (!s) return;
    const mine = r.lockUi.phase === 'locked' ? r.lockUi.lock : undefined;
    const text = s.state.status === 'pre' ? `Join me for ${s.meta.away.tricode} at ${s.meta.home.tricode} on Clutch.` : challengeText(s, mine);
    const url = `${location.origin}/room/${encodeURIComponent(roomId)}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: 'Clutch', text, url });
        return;
      }
    } catch (err) {
      if ((err as Error)?.name === 'AbortError') return;
    }
    try {
      await navigator.clipboard.writeText(`${text} ${url}`);
      flash('Link copied — send it to a friend');
    } catch {
      flash(url);
    }
  }, [s, r.lockUi, roomId, flash]);

  if (!s)
    return (
      <main className="room room--loading" aria-busy="true">
        <FanNav />
        <p className="muted">{r.status === 'reconnecting' ? 'Reconnecting to the room…' : 'Joining the room…'}</p>
        {r.joinError && <p className="alert">{r.joinError}</p>}
      </main>
    );

  const accents = { home: teamAccent(s.meta.home.tricode), away: teamAccent(s.meta.away.tricode) };
  const style = { '--home': accents.home, '--away': accents.away } as CSSProperties;
  const live = r.feed?.mode === 'live';

  const step = rewinder.step;
  const final = buzzer === 'court' ? finalPlay(r.plays) : undefined;
  const focus = final
    ? { seq: final.seq }
    : step && step.kind !== 'caught_up' && step.kind !== 'now' && step.seq !== undefined
      ? { seq: step.seq, uptoSeq: step.seq }
      : undefined;
  const overlay =
    buzzer === 'court' ? (
      <BuzzerOverlay s={s} />
    ) : step ? (
      <RewindOverlay steps={steps} index={rewinder.index} plays={r.plays} s={s} onNext={rewinder.next} onSkip={rewinder.skip} />
    ) : undefined;

  const callCard = (
    <CallCard s={s} lockUi={r.lockUi} onLock={r.lock} fanId={fan.fanId} onChallenge={r.challengeRoom} challenging={r.challenging} live={live} />
  );
  const showCardInline = !full;
  const recording = cam.phase === 'recording';

  const captionExtras = (e: GameEvent) => {
    const covering = takesCovering(r.takes, e.seq);
    const clip = clipFor(media, e.eventId);
    if (!covering.length && !clip) return null;
    return (
      <span className="caption-extras">
        <TakeBubbles takes={covering} seq={e.seq} urlOf={urlOf} onOpen={(take, offsetMs) => setPlayer({ take, ...(offsetMs !== undefined ? { offsetMs } : {}) })} />
        {clip && (
          <button className="watchplay" onClick={() => setHighlights({ start: clip.start })} data-testid="watch-play">
            ▶ Watch the play
          </button>
        )}
      </span>
    );
  };

  return (
    <main className={`room${full ? ' is-full' : ''}${live ? ' is-live' : ''}`} style={style} data-testid="room">
      {!full && <FanNav />}
      {!full && s.state.status === 'pre' && <div className="pregame-note" role="status"><span className="eyebrow">BEFORE THE BUZZER</span><h1>{s.meta.away.tricode} at {s.meta.home.tricode}</h1><p>{r.feed?.message ?? 'Waiting for tip-off'}. Keep this room open: scores and plays arrive automatically. Record your pregame take while you wait.</p></div>}
      <div ref={stageRef} className="room__stage">
        {!full && media && <OfficialVideo media={media} />}
        <Stage
          s={s}
          fanId={fan.fanId}
          plays={r.plays}
          backfilled={r.backfilled}
          {...(r.latestSeq !== undefined ? { liveSeq: r.latestSeq } : {})}
          anchors={anchors}
          reactions={r.reactions}
          full={full}
          onToggleFull={() => {
            setFull((f) => !f);
            setPillOpen(false);
          }}
          {...(focus ? { focus } : {})}
          {...(s.state.status === 'pre' ? { badge: { text: 'PREGAME · waiting for tip-off', live: false } } : feedBadge(r.feed) ? { badge: feedBadge(r.feed)! } : {})}
          overlay={overlay}
          captionExtras={captionExtras}
          layers={<>{beat && <BeatFlash key={beat.key} beat={beat} />}<DanmuLane flying={danmu.flying} takeById={takeById} urlOf={urlOf} onOpenTake={(take) => setPlayer({ take, offsetMs: take.marks.find((m) => m.seq === takeAnchorSeq(take, s.probability))?.offsetMs })} /></>}
          topActions={<>
            {full && <ReactionPicker onReact={r.react} />}
            <button
              className={`arena__cam${cam.stream ? ' is-on' : ''}${recording ? ' is-rec' : ''}`}
              onClick={toggleCam}
              aria-label={cam.stream ? 'Turn off React cam' : 'Turn on React cam'}
              aria-pressed={!!cam.stream}
              data-testid="cam-toggle"
            >
              <CamIcon />
            </button></>
          }
          footer={
            full ? (
              <FullSocial s={s} lockUi={r.lockUi} open={pillOpen} onToggle={() => setPillOpen((o) => !o)}>
                {callCard}
              </FullSocial>
            ) : undefined
          }
        />
        {!full && !live && <div className="replay-transport" role="group" aria-label="Replay controls">
          <span>REPLAY</span>
          <button disabled={controlling || s.replay.status === 'finished'} onClick={async () => {
            setControlling(true);
            try { await client.control(roomId, { action: s.replay.status === 'playing' ? 'pause' : 'play' }); }
            catch { flash('Couldn’t change playback. Try again.'); }
            finally { setControlling(false); }
          }}>{s.replay.status === 'playing' ? 'Pause' : s.replay.status === 'finished' ? 'Finished' : 'Play'}</button>
          <label>Speed <select aria-label="Replay speed" value={s.replay.speed} disabled={controlling} onChange={async (e) => {
            setControlling(true);
            try { await client.control(roomId, { action: 'speed', speed: Number(e.target.value) }); }
            catch { flash('Couldn’t change speed. Try again.'); }
            finally { setControlling(false); }
          }}>{[1, 5, 30, ...(![1, 5, 30].includes(s.replay.speed) ? [s.replay.speed] : [])].map((speed) => <option key={speed} value={speed}>{speed}×</option>)}</select></label>
        </div>}
        {!full && client.director && s.state.status !== 'pre' && (
          <button className="catchup-strip" onClick={() => navigate(`/room/${encodeURIComponent(roomId)}/catchup`)} data-testid="room-catchup">
            <span className="catchup-strip__play" aria-hidden="true">▶</span>
            <span className="catchup-strip__txt"><b>{s.resolution ? 'Your story' : 'Catch me up'}</b><small>{s.resolution ? 'Your call, the swing, the final · EN / हिन्दी' : 'What mattered to you so far · EN / हिन्दी'}</small></span>
            <span className="catchup-strip__time">60s</span>
          </button>
        )}
      </div>

      {!full && s.resolution && dismissed && <section className="result-summary" aria-label="Game result">
        <div><span className="eyebrow">FINAL · {s.meta.away.tricode} {s.resolution.finalAway}–{s.resolution.finalHome} {s.meta.home.tricode}</span><h2>{resolutionCopy(s.resolution, fan.fanId, r.lockUi.phase === 'locked' ? r.lockUi.lock : undefined).headline}</h2></div>
        <div className="result-summary__actions">
          <button className="btn btn--small btn--ghost" onClick={() => setDismissed(false)}>View result</button>
          {r.lockUi.phase === 'locked' && <button className="btn btn--small btn--primary" onClick={() => navigate(`/room/${encodeURIComponent(roomId)}/receipt`)}>Get your receipt</button>}
        </div>
      </section>}
      {!full && (
        <Studio
          recording={recording}
          elapsed={cam.elapsed}
          onTake={recordTake}
          onReact={r.react}
          media={media}
          onHighlights={() => setHighlights({})}
          takes={r.takes.length}
        />
      )}
      {!full && s.state.status !== 'pre' && <Odds s={s} />}
      {showCardInline && s.state.status !== 'pre' && callCard}
      {!full && myTake && (
        <ReelCard s={s} take={myTake} plays={r.plays} onOpen={() => setReelTake(myTake)} />
      )}
      {!full && r.takes.length > 0 && <section className="room-takes" aria-label="Room takes">
        <div className="section-heading"><h2>From the room <span>{r.takes.length}</span></h2><AppLink href="/takes">My takes ↗</AppLink></div>
        <div className="room-takes__rail">{[...r.takes].reverse().map((take) => <article className="room-take" key={take.takeId}>
          <button onClick={() => setPlayer({ take })} aria-label={`Play ${take.fanId === fan.fanId ? 'your' : take.displayName + '’s'} take`}>
            <video src={urlOf(take)} preload="metadata" playsInline muted /><span aria-hidden="true">▶</span>
            <b>{take.fanId === fan.fanId ? 'You' : firstName(take.displayName)}</b><small>{Math.round(take.durationMs / 1000)}s · {take.startedAtSeq < 0 ? 'Pregame' : `${take.marks.length} plays`}</small>
          </button>
          {take.fanId === fan.fanId && selectReelSegments(s.probability, take.marks, { durationMs: take.durationMs, plays: r.plays }).segments.length > 0 && <button className="room-take__remix" onClick={() => setReelTake(take)}>Make reel ↗</button>}
        </article>)}</div>
      </section>}
      {!full && (
        <>
          <button className="challenge" onClick={share} data-testid="challenge">
            <span className="challenge__icon" aria-hidden="true">
              ⚔
            </span>
            <span>
              <b>{s.state.status === 'pre' ? 'Invite a friend' : 'Challenge a friend'}</b>
              <small>{s.state.status === 'pre' ? `Bring someone courtside for ${s.meta.away.tricode} at ${s.meta.home.tricode}.` : challengeText(s, r.lockUi.phase === 'locked' ? r.lockUi.lock : undefined)}</small>
            </span>
          </button>
          <PlayLog s={s} plays={r.plays} anchors={anchors} takes={r.takes} urlOf={urlOf} onOpenTake={(take, offsetMs) => setPlayer({ take, ...(offsetMs !== undefined ? { offsetMs } : {}) })} />
          <p className="room__foot">
            <span>{live ? 'Live play-by-play from ESPN. The court is drawn from it.' : 'A replay of the real play-by-play feed.'}</span>
            <a className="watchlive" href={WATCH_LIVE_URL} target="_blank" rel="noopener noreferrer">
              Watch live (NBA) <span aria-hidden="true">↗</span>
            </a>
          </p>
        </>
      )}

      {cam.stream && cam.phase !== 'review' && <CamBubble cam={cam} onRecord={() => cam.start(asOfRef.current)} onClose={cam.disable} />}
      {consent && (
        <CamConsent
          quick={consent.quick}
          onCancel={() => setConsent(undefined)}
          onAllow={() => {
            store.set(CONSENT_KEY, '1');
            const q = consent.quick;
            setConsent(undefined);
            void beginRecording(q);
          }}
        />
      )}
      {cam.phase === 'review' && cam.recording && (
        <TakeReview
          rec={cam.recording}
          playsCovered={cam.recording.marks.length}
          busy={publishing}
          {...(publishError ? { error: publishError } : {})}
          onPublish={publish}
          onDiscard={cam.discard}
        />
      )}
      {player && <TakePlayer take={player.take} url={urlOf(player.take)} {...(player.offsetMs !== undefined ? { offsetMs: player.offsetMs } : {})} onClose={() => setPlayer(undefined)} />}
      {highlights && media && <HighlightsSheet media={media} {...(highlights.start !== undefined ? { start: highlights.start } : {})} onClose={() => setHighlights(undefined)} />}
      {reelTake && (
        <ReelSheet
          s={s}
          fan={fan}
          take={reelTake}
          {...(localUrls[reelTake.takeId] ? { localUrl: localUrls[reelTake.takeId] } : {})}
          takes={r.takes}
          plays={r.plays}
          lockUi={r.lockUi}
          urlOf={urlOf}
          onClose={() => setReelTake(undefined)}
        />
      )}

      {r.status === 'reconnecting' && <div className="toast">Reconnecting…</div>}
      {toast && (
        <div className="toast toast--ok" role="status">
          {toast}
        </div>
      )}

      {s.resolution && buzzer === 'card' && !dismissed && (
        <ResolutionOverlay
          s={s}
          fan={fan}
          lockUi={r.lockUi}
          onClose={() => setDismissed(true)}
          roomId={roomId}
          {...(myTake ? { onReel: () => (setDismissed(true), setReelTake(myTake)) } : {})}
          {...(media ? { onHighlights: () => setHighlights({}) } : {})}
          {...(client.director ? { onStory: () => navigate(`/room/${encodeURIComponent(roomId)}/catchup`) } : {})}
        />
      )}
    </main>
  );
}

function CamIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <rect x="2.5" y="6" width="13" height="12" rx="3" fill="none" stroke="currentColor" strokeWidth="2" />
      <path d="M15.5 10.5 21 7.5v9l-5.5-3" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    </svg>
  );
}

// ───────────── Reaction studio strip ─────────────

function Studio({
  recording,
  elapsed,
  onTake,
  onReact,
  media,
  onHighlights,
  takes,
}: {
  recording: boolean;
  elapsed: number;
  onTake: () => void;
  onReact: (emoji: ReactionEmoji) => void;
  media?: GameMedia;
  onHighlights: () => void;
  takes: number;
}) {
  const left = Math.max(0, Math.ceil((QUICK_TAKE_MS - elapsed) / 1000));
  return (
    <section className="studio" aria-label="Reaction studio" data-testid="studio">
      <button className={`studio__take${recording ? ' is-rec' : ''}`} onClick={onTake} data-testid="record-take">
        <span className="studio__dot" aria-hidden="true" />
        <span className="studio__label">
          <b>{recording ? 'Recording…' : 'Record my take'}</b>
          <small>{recording ? `tap to stop · ${left}s` : takes > 0 ? `${takes} take${takes === 1 ? '' : 's'} in this room` : '10 seconds · your face on the play'}</small>
        </span>
      </button>
      <ReactionPicker onReact={onReact} />
      {media && (
        <button className="studio__hl" onClick={onHighlights} data-testid="highlights-open">
          <span aria-hidden="true">▶</span> Highlights
        </button>
      )}
    </section>
  );
}

// ───────────── Clutch Reel card ─────────────

function ReelCard({ s, take, plays, onOpen }: { s: RoomSnapshot; take: Take; plays: readonly GameEvent[]; onOpen: () => void }) {
  const sel = useMemo(() => selectReelSegments(s.probability, take.marks, { durationMs: take.durationMs, plays }), [s.probability, take, plays]);
  const picks = sel.segments.flatMap((g) => g.beats).map((g) => plays.find((e) => e.seq === g.seq)).filter((e): e is GameEvent => !!e);
  return (
    <section className="reelcard" data-testid="reel-card">
      <div className="reelcard__faces" aria-hidden="true">
        {picks.slice(0, 3).map((e) => (
          <Headshot key={e.seq} personId={e.personId} source={s.meta.source} name={e.playerName} size={44} ring={e.side ? `var(--${e.side})` : undefined} />
        ))}
      </div>
      <div className="reelcard__txt">
        <b>Your Clutch Reel</b>
        <small>{picks.length ? `Your reaction × the ${picks.length} play${picks.length === 1 ? '' : 's'} that swung it` : 'Keep rolling through the next big play'}</small>
      </div>
      <button className="btn btn--primary btn--small" onClick={onOpen} data-testid="reel-make">
        Make reel
      </button>
    </section>
  );
}

// ───────────── Full-screen social overlay ─────────────

function FullSocial({
  s,
  lockUi,
  open,
  onToggle,
  children,
}: {
  s: RoomSnapshot;
  lockUi: LockUi;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  const call = s.call;
  if (!call) return null;
  const mine = lockUi.phase === 'locked' ? lockUi.lock : undefined;
  const short = call.question.replace(/ and win\?$/, '?');
  return (
    <div className={`fullsocial${open ? ' is-open' : ''}`}>
      {open && (
        <div className="fullsocial__sheet" role="dialog" aria-label="Your call">
          <button className="fullsocial__close" onClick={onToggle} aria-label="Collapse call">
            ⌄
          </button>
          {children}
        </div>
      )}
      {!open && (
        <button className="callpill" onClick={onToggle} data-testid="call-pill">
          <span className="callpill__q">{short}</span>
          {mine ? (
            <span className="callpill__mine" style={{ '--c': `var(--${choiceSide(call, mine.choice)})` } as CSSProperties}>
              You: {choiceLabel(call, mine.choice)} 🔒
            </span>
          ) : call.status === 'open' ? (
            <span className="callpill__yn">
              <b>YES</b> / <b>NO</b>
            </span>
          ) : (
            <span className="callpill__yn">Closed</span>
          )}
        </button>
      )}
    </div>
  );
}

// ───────────── Fans vs the model (compact) ─────────────

function Odds({ s }: { s: RoomSnapshot }) {
  return (
    <section className="odds">
      <FansVsModelRow s={s} />
      <ChanceBar s={s} />
    </section>
  );
}

function FansVsModelRow({ s }: { s: RoomSnapshot }) {
  const v = fansVsModel(s);
  if (!v) return <span className="fvm" />;
  return (
    <p className="fvm" data-testid="fans-vs-model">
      <span className="fvm__room">{v.room}</span>
      <span className="fvm__vs">vs</span>
      <span className="fvm__model">{v.model ?? 'Model loading'}</span>
    </p>
  );
}

function ChanceBar({ s }: { s: RoomSnapshot }) {
  const v = chanceView(s);
  if (v.status === 'loading')
    return (
      <section className="chance chance--loading" aria-label="Win chance" data-testid="chance">
        <div className="chance__row">
          <span className="chance__label">Win chance</span>
          <span className="chance__value chance__value--muted">Model loading</span>
        </div>
        <div className="chance__track chance__track--empty" />
      </section>
    );
  const name = sideName(s, v.side);
  const accent = v.side === 'home' ? 'var(--home)' : 'var(--away)';
  return (
    <section className="chance" aria-label={`${name} win chance ${v.pct}%`} data-testid="chance">
      <div className="chance__row">
        <span className="chance__label">{name} win chance</span>
        <span className="chance__value">
          {v.delta !== undefined && (
            <span key={v.atSeq} className={`chance__delta ${v.delta > 0 ? 'is-up' : 'is-down'}`}>
              {v.delta > 0 ? '+' : '−'}
              {Math.abs(v.delta)}%
            </span>
          )}
          <span data-testid="chance-pct">{v.pct}%</span>
        </span>
      </div>
      <div className="chance__track">
        <div className="chance__fill" style={{ width: `${v.pct}%`, background: accent }} />
        <span className="chance__mid" />
      </div>
    </section>
  );
}

// ───────────── Play log with reaction stacks ─────────────

function PlayLog({
  s,
  plays,
  anchors,
  takes,
  urlOf,
  onOpenTake,
}: {
  s: RoomSnapshot;
  plays: readonly GameEvent[];
  anchors: Map<string, ReactionStack[]>;
  takes: Take[];
  urlOf: (t: Take) => string;
  onOpenTake: (t: Take, offsetMs?: number) => void;
}) {
  const name = (sd: Side) => sideName(s, sd);
  const fromLog = plays.filter((e) => playCaption(e, name) && e.type !== 'period_start').slice(-5).reverse();
  const items: { eventId: string; seq?: number; period: number; clockSec: number; side?: Side; text: string; points?: number; personId?: number; playerName?: string }[] = fromLog.length
    ? fromLog.map((e) => ({
        eventId: e.eventId,
        seq: e.seq,
        period: e.period,
        clockSec: e.clockSec,
        side: e.side,
        text: e.description,
        points: e.made ? e.points : undefined,
        personId: e.personId,
        playerName: e.playerName,
      }))
    : s.state.lastPlays.slice(0, 4);
  return (
    <section className="plays" aria-label="Last plays" aria-live="polite">
      <h3 className="plays__h">Play-by-play</h3>
      {items.length === 0 && <p className="muted">Waiting for the next play…</p>}
      <ol>
        {items.map((p, i) => {
          const st = anchors.get(p.eventId);
          return (
            <li key={p.eventId} className={`play${i === 0 ? ' is-new' : ''}`}>
              <span className={`play__tick play__tick--${p.side ?? 'none'}`} />
              {p.personId ? (
                <Headshot personId={p.personId} source={s.meta.source} name={p.playerName} size={30} ring={p.side ? `var(--${p.side})` : undefined} className="play__head" />
              ) : (
                <span className="play__head play__head--none" />
              )}
              <span className="play__body">
                <span className="play__text">{p.text}</span>
                <span className="play__clock">{formatClock(p.period, p.clockSec)}</span>
              </span>
              {p.seq !== undefined && <TakeBubbles takes={takesCovering(takes, p.seq)} seq={p.seq} urlOf={urlOf} onOpen={onOpenTake} max={2} />}
              {st && st.length > 0 ? <Stacks stacks={st} /> : null}
              {p.points ? <span className="play__pts">+{p.points}</span> : null}
            </li>
          );
        })}
      </ol>
    </section>
  );
}

// ───────────── The call ─────────────

function CallCard({
  s,
  lockUi,
  onLock,
  fanId,
  onChallenge,
  challenging,
  live,
}: {
  s: RoomSnapshot;
  lockUi: LockUi;
  onLock: (c: 'yes' | 'no') => void;
  fanId: string;
  onChallenge: () => void;
  challenging: boolean;
  live: boolean;
}) {
  const call = s.call;
  if (!call) {
    const canChallenge = s.state.status === 'live';
    return (
      <section className="call call--waiting" data-testid="call-waiting">
        <p className="call__waiting">{live ? 'Clutch is watching this one. The call opens when the moment does —' : 'The call opens when the moment does.'}</p>
        {canChallenge && (
          <button className="btn btn--primary btn--block challenge-room" onClick={onChallenge} disabled={challenging} data-testid="challenge-room">
            {challenging ? 'Opening…' : '⚔ Challenge the room'}
          </button>
        )}
        {canChallenge && <p className="call__hint">Ask everyone here who wins. Same rules: one call each, locked by the server.</p>}
      </section>
    );
  }
  const origin =
    call.openedBy?.kind === 'fan' ? (call.openedBy.fanId === fanId ? 'You opened this' : `Opened by ${firstName(call.openedBy.displayName)}`) : 'Clutch spotted this';
  const closed = call.status !== 'open';
  const chips = friendCalls(s, fanId);
  const btn = (c: 'yes' | 'no') => {
    const sd = choiceSide(call, c);
    const accent = sd === 'home' ? teamAccent(s.meta.home.tricode) : teamAccent(s.meta.away.tricode);
    const pressed = lockUi.phase === 'pending' && lockUi.choice === c;
    const chosen = lockUi.phase === 'locked' && lockUi.lock.choice === c;
    const dim =
      (lockUi.phase === 'pending' && lockUi.choice !== c) || (lockUi.phase === 'locked' && lockUi.lock.choice !== c);
    return (
      <button
        className={`callbtn${pressed ? ' is-pressed' : ''}${chosen ? ' is-chosen' : ''}${dim ? ' is-dim' : ''}`}
        style={{ '--accent': accent, '--on-accent': readableOn(accent) } as CSSProperties}
        disabled={closed || lockUi.phase === 'pending' || lockUi.phase === 'locked'}
        onClick={() => onLock(c)}
        data-testid={`call-${c}`}
        aria-pressed={pressed || chosen}
      >
        <span className="callbtn__yn">{c === 'yes' ? 'YES' : 'NO'}</span>
        <span className="callbtn__team">{choiceLabel(call, c)}</span>
        {pressed && <span className="callbtn__locking">Locking…</span>}
      </button>
    );
  };
  return (
    <section className={`call${closed ? ' is-closed' : ''}`} aria-label="Your call" data-testid="call">
      <div className="call__head">
        <span className={`call__origin${call.openedBy?.kind === 'fan' ? ' is-fan' : ''}`} data-testid="call-origin">
          {call.openedBy?.kind === 'fan' ? '⚔' : '✦'} {origin}
        </span>
        <h2 className="call__q">{call.question}</h2>
        <span className="call__cutoff">
          {call.status === 'open' ? `Locks at ${cutoffLabel(call)}` : `Calls closed at ${cutoffLabel(call)}`}
        </span>
      </div>
      {chips.length > 0 && (
        <ul className="call__friends">
          {chips.slice(-4).map((c, i) => (
            <li key={`${i}-${c}`} className="chip chip--friend">
              {c}
            </li>
          ))}
          {chips.length > 4 && <li className="chip chip--more">+{chips.length - 4} more calls</li>}
        </ul>
      )}
      <div className="call__btns">
        {btn('yes')}
        {btn('no')}
      </div>
      <div className="call__status" aria-live="assertive">
        {lockUi.phase === 'locked' && (
          <p className="stamp" data-testid="lock-stamp" ref={revealAboveReactbar}>
            {lockStamp(lockUi.lock)}
          </p>
        )}
        {lockUi.phase === 'rejected' && (
          <p className="alert" role="alert" data-testid="lock-rejected" ref={revealAboveReactbar}>
            {lockUi.message}
          </p>
        )}
        {lockUi.phase === 'idle' && !closed && <p className="call__hint">One tap. Server-locked. No take-backs.</p>}
      </div>
    </section>
  );
}

// ───────────── Resolution ─────────────

function ResolutionOverlay({
  s,
  fan,
  lockUi,
  onClose,
  roomId,
  onReel,
  onHighlights,
  onStory,
}: {
  s: RoomSnapshot;
  fan: Fan;
  lockUi: LockUi;
  onClose: () => void;
  roomId: string;
  onReel?: () => void;
  onHighlights?: () => void;
  onStory?: () => void;
}) {
  const res = s.resolution!;
  const myLock = lockUi.phase === 'locked' ? lockUi.lock : undefined;
  const copy = resolutionCopy(res, fan.fanId, myLock);
  useEffect(() => {
    if (copy.tone === 'correct') celebrate('big');
  }, [copy.tone]);
  const call = s.call;
  const winner: Side = res.finalHome > res.finalAway ? 'home' : 'away';
  return (
    <div className={`resolve resolve--${copy.tone}`} role="dialog" aria-modal="true" aria-label="Final result" data-testid="resolution">
      <div className="resolve__inner">
        <p className="resolve__final">FINAL</p>
        <p className="resolve__score">
          <span className={winner === 'away' ? 'is-win' : ''}>
            {s.meta.away.tricode} {res.finalAway}
          </span>
          <span className="resolve__dash">–</span>
          <span className={winner === 'home' ? 'is-win' : ''}>
            {res.finalHome} {s.meta.home.tricode}
          </span>
        </p>
        <h2 className="resolve__headline" data-testid="resolution-headline">
          {copy.headline}
        </h2>
        {copy.sub && <p className="resolve__sub">{copy.sub}</p>}
        {call && res.results.length > 0 && (
          <ul className="resolve__list" data-testid="group-results">
            {res.results.map((x) => (
              <li key={x.fanId} className={x.correct ? 'is-right' : ''}>
                <span className="avatar avatar--sm">{initials(x.displayName)}</span>
                <span className="resolve__name">{x.fanId === fan.fanId ? 'You' : x.displayName}</span>
                <span className="resolve__pick">{choiceLabel(call, x.choice)}</span>
                <span className="resolve__mark">{x.correct ? 'Called it' : '—'}</span>
              </li>
            ))}
          </ul>
        )}
        <div className="resolve__actions">
          {onStory && (
            <button className="btn btn--primary btn--block resolve__story" onClick={onStory} data-testid="resolution-catchup">
              ▶ Your story · 60 sec
            </button>
          )}
          {onReel && (
            <button className={`btn ${onStory ? 'btn--ghost' : 'btn--primary'} btn--block resolve__reel`} onClick={onReel} data-testid="resolution-reel">
              Make my Clutch Reel
            </button>
          )}
          {onHighlights && (
            <button className="btn btn--ghost btn--block" onClick={onHighlights} data-testid="resolution-highlights">
              ▶ Official highlights
            </button>
          )}
          {myLock && (
            <button className={`btn ${onReel || onStory ? 'btn--ghost' : 'btn--primary'} btn--block`} onClick={() => navigate(`/room/${encodeURIComponent(roomId)}/receipt`)}>
              Get your receipt
            </button>
          )}
          <button className="btn btn--ghost btn--block" onClick={onClose}>
            Back to the room
          </button>
        </div>
      </div>
    </div>
  );
}

// ───────────── Big-moment beat ─────────────

function BeatFlash({ beat }: { beat: Beat & { label: string } }) {
  return (
    <div className={`beat beat--${beat.kind}${beat.mine ? ' is-mine' : ''}`} style={{ '--beat': `var(--${beat.side})` } as CSSProperties} data-testid="beat" aria-hidden="true">
      <span className="beat__flash" />
      <span className="beat__label">{beat.label}</span>
    </div>
  );
}
